import {
  FOC_COP_LAYERS_TOUR_STEPS,
  type FocCopLayersTourStepId,
  type FocCopLayersTourSurface,
} from './focCopLayersTourSteps';

/**
 * The tour runs outside React, so Layers, AORs, and the hub map layout
 * register the UI moves they alone can perform. Everything is optional: a
 * step whose host is not mounted simply has nothing to reveal.
 */
export type FocCopLayersTourApi = {
  getOpenTab: () => string;
  showLayers: () => void;
  openAors: () => void;
  isSitStatOpen: () => boolean;
  openSitStatViews: () => void;
  exitSitStatToAors: () => void;
  /** LayersViewContent */
  closeSidePanels: () => void;
  /** ViewLayersPanel */
  showOverview: () => void;
  openFirstLayerDetail: () => void;
  ensureFolderVisible: () => void;
  expandFirstExternalLayer: () => void;
  /** AORs tab (subset of org-workspace reveals) */
  showAorsOverview: () => void;
  expandFirstDistrict: () => void;
  revealSector: () => void;
  showSectorDetails: () => void;
  showDistrictDetails: () => void;
  showBreadcrumbs: () => void;
  showAreaFilters: () => void;
};

let api: Partial<FocCopLayersTourApi> = {};
/** A step waiting for Layers/AORs/SitStat to remount after a surface switch. */
let pendingStep: FocCopLayersTourStepId | null = null;

export function registerFocCopLayersTourApi(
  partial: Partial<FocCopLayersTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    const needed = surfaceFor(step);
    if (currentSurface() === needed) {
      pendingStep = null;
      prepareFocCopLayersStep(step);
    }
  }
  return () => {
    for (const key of Object.keys(partial) as (keyof FocCopLayersTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocCopLayersTourApi(): Partial<FocCopLayersTourApi> {
  return api;
}

function surfaceFor(id: FocCopLayersTourStepId): FocCopLayersTourSurface {
  return (
    FOC_COP_LAYERS_TOUR_STEPS.find((step) => step.id === id)?.surface ??
    'layers'
  );
}

function currentSurface(): FocCopLayersTourSurface {
  const host = getFocCopLayersTourApi();
  if (host.isSitStatOpen?.()) return 'sitstat';
  const tab = host.getOpenTab?.() ?? '';
  if (tab === 'AORs') return 'aors';
  return 'layers';
}

function ensureSurface(id: FocCopLayersTourStepId): boolean {
  const needed = surfaceFor(id);
  const current = currentSurface();
  if (current === needed) {
    pendingStep = null;
    return true;
  }

  const host = getFocCopLayersTourApi();
  pendingStep = id;

  if (needed === 'sitstat') {
    host.openSitStatViews?.();
    return false;
  }

  if (current === 'sitstat') {
    host.exitSitStatToAors?.();
    if (needed === 'aors') return false;
    // After exiting SitStat we land on AORs; chain to Layers if required.
    if (needed === 'layers') {
      host.showLayers?.();
    }
    return false;
  }

  if (needed === 'aors') {
    host.openAors?.();
    return false;
  }

  host.showLayers?.();
  return false;
}

/** Puts the screen in the state the step highlights. */
export function prepareFocCopLayersStep(id: FocCopLayersTourStepId) {
  if (!ensureSurface(id)) return;

  const host = getFocCopLayersTourApi();
  const surface = surfaceFor(id);

  if (surface === 'sitstat') {
    return;
  }

  if (surface === 'aors') {
    switch (id) {
      case 'sectionIntro55':
      case 'cop008':
      case 'cop008b':
      case 'cop009':
        host.showAorsOverview?.();
        return;
      case 'cop008a':
      case 'cop008e':
      case 'cop008f':
        host.showAreaFilters?.();
        return;
      case 'cop008c':
        host.showSectorDetails?.();
        return;
      case 'cop008d':
        host.showDistrictDetails?.();
        return;
      case 'cop009a':
        host.showBreadcrumbs?.();
        return;
      default:
        host.showAorsOverview?.();
    }
    return;
  }

  // layers surface
  switch (id) {
    case 'sectionIntro54':
    case 'cop006':
    case 'cop006c':
      host.closeSidePanels?.();
      host.showOverview?.();
      return;
    case 'cop006a':
      host.closeSidePanels?.();
      host.showOverview?.();
      host.ensureFolderVisible?.();
      return;
    case 'cop006b':
    case 'cop006d':
      host.closeSidePanels?.();
      host.showOverview?.();
      return;
    case 'cop007':
    case 'cop007b':
      host.closeSidePanels?.();
      host.openFirstLayerDetail?.();
      return;
    case 'cop007a':
      host.closeSidePanels?.();
      host.showOverview?.();
      host.expandFirstExternalLayer?.();
      return;
    case 'focFeedback':
      host.closeSidePanels?.();
      host.showOverview?.();
      return;
    default:
      host.closeSidePanels?.();
      host.showOverview?.();
  }
}

/** Test helper to clear the module-level bridge. */
export function resetFocCopLayersTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
